import { Message } from "primereact/message";
import {
  EngineBenefit,
  IEngineBenefitSelections,
} from "types/BenefitSelection/BenefitSelection";

import { quotedCopayListPick } from "./engineCopayListPick";

/**
 * CLE-2951 — the benefit engine can quote a copay or co-insurance that did NOT come from the payer's
 * answer for the account's own care setting:
 *
 *   · HISTORY    — what this plan reported on earlier checks, because this check said nothing usable;
 *   · SUBSTITUTE — substituted from another care setting the account declares as its substitute, because
 *                  the payer gave no answer for the account's own one.
 *
 * Ani (2026-10-09, CLE-2951 comment a894a60d): "for current design just use our existing yellow message
 * window with some wording". So the banner is the rung's plain-language sentence followed by the
 * engine's own `provenance.reason`, which names the actual sections.
 *
 * CLE-2816 adds a third case, for the flat copay only: the payer lists a copay per procedure and the
 * engine fell back to the list's generic OUTPATIENT HOSPITAL CARE item (`provenance.copayList.fallback`).
 *
 * 🚨 Gated on `quoted`. On an account still quoted by legacy every `quoted` is false and the engine's
 * value may differ from the number on screen — a banner there would explain a number the patient was
 * never given.
 */

export const ENGINE_PROVENANCE_HISTORY_TEXT =
  "This value did not come from this eligibility response. It is based on what this plan reported on earlier checks. Please confirm before continuing.";

export const ENGINE_PROVENANCE_SUBSTITUTE_TEXT =
  "The payer gave no answer for this account's own care setting. This value is substituted from another section of the response, which this account uses as its substitute.";

/**
 * CLE-2816 — the payer lists a copay per procedure, no procedure on the estimate maps to an item on the
 * list, so the engine quoted the list's generic item. Ani's wording (2026-10-30, comment 914d1dc1), tidied.
 * Deliberately WITHOUT the engine's `reason`: the sentence is the whole message she asked for.
 */
export const ENGINE_PROVENANCE_COPAY_LIST_FALLBACK_TEXT =
  "No specific service match found. Outpatient Hospital Care copay selected.";

const RUNG_TEXT: Partial<Record<string, string>> = {
  HISTORY: ENGINE_PROVENANCE_HISTORY_TEXT,
  SUBSTITUTE: ENGINE_PROVENANCE_SUBSTITUTE_TEXT,
};

/** The stored eligibility response (`benefitDetails.response`), or as much of it as we read. */
interface IResponseWithEngineSelections {
  engineBenefitSelections?: IEngineBenefitSelections | null;
}

export const engineProvenanceBannerText = (
  response: IResponseWithEngineSelections | null | undefined,
  benefit: EngineBenefit
): string | null => {
  const selection = response?.engineBenefitSelections?.benefits?.[benefit];
  if (!selection || selection.quoted !== true) return null;

  // CLE-2816 — same `quoted` gate (above); `quotedCopayListPick` repeats it and also requires a read
  // off the list (a correcting override that replaced the amount keeps `copayList` but is not one).
  if (benefit === "FLAT_COPAY" && quotedCopayListPick(response)?.fallback) {
    return ENGINE_PROVENANCE_COPAY_LIST_FALLBACK_TEXT;
  }

  const rungText = RUNG_TEXT[selection.provenance?.rung];
  if (!rungText) return null;

  const reason =
    typeof selection.provenance.reason === "string"
      ? selection.provenance.reason.trim()
      : "";
  return reason ? `${rungText} ${reason}` : rungText;
};

export function EngineProvenanceBanner({
  response,
  benefit,
  testId,
}: {
  response: IResponseWithEngineSelections | null | undefined;
  benefit: EngineBenefit;
  testId: string;
}) {
  const text = engineProvenanceBannerText(response, benefit);
  if (!text) return null;
  return (
    <Message
      severity="warn"
      className="mt-2"
      data-testid={testId}
      text={text}
    />
  );
}
